import { useEffect, useState } from 'react';

import type { ConnectorCapabilityVerdict } from './connectorCapabilityResolution';
import { TravelTicket } from './queries';

const MAX_TIMEOUT_MS = 2_147_483_647;

export interface EffectiveTicketResult {
  ready: boolean;
  tickets: TravelTicket[];
  hasNetworkTicketUrls: boolean;
  networkTicketUrlsExpired: boolean;
}

export function isNetworkTicketUrl(url?: string): boolean {
  if (!url) return false;

  try {
    const parsed = new URL(url);
    return (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      !parsed.username &&
      !parsed.password
    );
  } catch {
    return false;
  }
}

export function millisUntilTicketExpiry(expiresAt?: string | null): number {
  if (!expiresAt) return Number.POSITIVE_INFINITY;
  const at = Date.parse(expiresAt);
  return Number.isFinite(at) ? at - Date.now() : Number.POSITIVE_INFINITY;
}

export function filterExpiredNetworkTicketUrls(
  tickets: TravelTicket[],
  expired: boolean,
): { tickets: TravelTicket[]; removedNetworkUrl: boolean } {
  let removedNetworkUrl = false;

  const effective = tickets
    .map((ticket) => {
      if (!expired || !isNetworkTicketUrl(ticket.url)) return { ...ticket };

      removedNetworkUrl = true;
      const withoutUrl = { ...ticket };
      delete withoutUrl.url;
      return withoutUrl;
    })
    .filter(isActionableTicket);

  return { tickets: effective, removedNetworkUrl };
}

export function effectiveTicketResult(input: {
  ready: boolean;
  tickets?: TravelTicket[];
  expiresAt?: string | null;
}): EffectiveTicketResult {
  const expired = millisUntilTicketExpiry(input.expiresAt) <= 0;
  const { tickets, removedNetworkUrl } = filterExpiredNetworkTicketUrls(
    input.tickets ?? [],
    expired,
  );

  return {
    ready: input.ready === true && tickets.length > 0,
    tickets,
    hasNetworkTicketUrls: tickets.some((ticket) => isNetworkTicketUrl(ticket.url)),
    networkTicketUrlsExpired: removedNetworkUrl,
  };
}

/**
 * Qué controles de tickets se pueden dibujar para una reserva. Es UNA sola
 * expresión para todas las superficies (listado, detalle, modal de reserva y
 * el propio `BookingTickets`), porque cada superficie tiene su propio botón que
 * termina en el mismo `GET /tickets`, y gatear tres de cinco es no gatear.
 *
 * La regla: MOSTRAR tickets ya emitidos no cuesta nada (son datos guardados);
 * PEDIRLE tickets al proveedor —la primera vez o para refrescar URLs firmadas
 * vencidas— es una llamada al hub que devuelve un 400 pelado si el conector no
 * los expone, y se hace SOLO con un `supported` explícito. `unsupported` se le
 * explica al agente; `unknown` no se acusa al proveedor de nada.
 */
export interface TicketFetchSurfaces {
  /** Se puede pedirle tickets al proveedor (primera vez o volver a pedir). */
  canFetch: boolean;
  /** Las URLs firmadas vencieron Y se puede pedir otras: botón de refresh. */
  canRefreshExpired: boolean;
  /** El conector dijo que NO expone tickets por API. */
  showUnsupportedHint: boolean;
  /** Lo emitido que sigue siendo utilizable; no depende del veredicto. */
  visibleTickets: TravelTicket[];
}

export interface TicketRenderRow {
  key: string;
  ticket: TravelTicket;
}

const TICKET_DIRECTION_ORDER: Record<
  NonNullable<TravelTicket['direction']>,
  number
> = {
  outbound: 0,
  return: 1,
};

export function associatedTicketDirection(
  ticket: TravelTicket,
): TravelTicket['direction'] | undefined {
  return ticket.legId && ticket.direction ? ticket.direction : undefined;
}

function ticketIdentity(ticket: TravelTicket): string {
  return [
    ticket.legId ?? '',
    ticket.direction ?? '',
    ticket.kind,
    ticket.reference ?? '',
    ticket.url ?? '',
    ticket.language ?? '',
    ticket.description ?? '',
    ticket.esimInstallation?.iccid ?? '',
  ].join('|');
}

/**
 * Orden contractual de documentos: ida, vuelta y finalmente los recursos del
 * booking completo o sin asociación demostrable. El índice sólo desempata el
 * orden original; nunca decide a qué tramo pertenece un ticket.
 *
 * La ocurrencia hace única una duplicación literal sin atar la identidad de
 * las demás filas a su posición en la lista.
 */
export function ticketRenderRows(tickets: TravelTicket[]): TicketRenderRow[] {
  const occurrences = new Map<string, number>();
  return tickets
    .map((ticket, index) => ({ ticket, index }))
    .sort((a, b) => {
      const aDirection = associatedTicketDirection(a.ticket);
      const bDirection = associatedTicketDirection(b.ticket);
      const aOrder = aDirection ? TICKET_DIRECTION_ORDER[aDirection] : 2;
      const bOrder = bDirection ? TICKET_DIRECTION_ORDER[bDirection] : 2;
      return aOrder - bOrder || a.index - b.index;
    })
    .map(({ ticket }) => {
      const identity = ticketIdentity(ticket);
      const occurrence = occurrences.get(identity) ?? 0;
      occurrences.set(identity, occurrence + 1);
      return { key: `${identity}|${occurrence}`, ticket };
    });
}

export function ticketFetchSurfaces({
  verdict,
  effective,
}: {
  verdict: ConnectorCapabilityVerdict;
  effective: EffectiveTicketResult;
}): TicketFetchSurfaces {
  const canFetch = verdict === 'supported';
  return {
    canFetch,
    canRefreshExpired: canFetch && effective.networkTicketUrlsExpired,
    showUnsupportedHint: verdict === 'unsupported',
    visibleTickets: effective.tickets,
  };
}

export function useTicketExpiryTimer(expiresAt?: string | null): void {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const remaining = millisUntilTicketExpiry(expiresAt);
    if (!Number.isFinite(remaining) || remaining <= 0) return undefined;

    const timer = setTimeout(
      () => setTick((value) => value + 1),
      Math.min(remaining, MAX_TIMEOUT_MS),
    );
    return () => clearTimeout(timer);
  }, [expiresAt, tick]);
}

function isActionableTicket(ticket: TravelTicket): boolean {
  return Boolean(ticket.url || ticket.reference || ticket.esimInstallation);
}

/**
 * Con qué se rotula un documento del viaje.
 *
 * El texto del OPERADOR manda siempre: describe el documento concreto mejor
 * que cualquier genérico nuestro. Sin él, el rótulo sale del `kind` — pero el
 * `file` y `selfservice` son recursos genéricos: no se deduce su semántica del
 * vertical ni del proveedor. La descripción publicada sigue siendo la única
 * etiqueta específica autoritativa.
 */
export function ticketLabelKey(
  ticket: Pick<TravelTicket, 'kind' | 'description'>,
  _vertical: string,
): string | null {
  if (ticket.description) return null;
  return `travelServices.booking.ticketKinds.${ticket.kind}`;
}

export type TicketCopyOutcome = 'copied' | 'failed';

/**
 * Qué pasó la última vez que se intentó copiar un link, y CUÁL link. Se guarda
 * la URL porque los links firmados rotan: "¡Copiado!" es verdad para el link
 * que fue al portapapeles, no para el que la fila muestra ahora.
 */
export type TicketCopyState = { url: string; outcome: TicketCopyOutcome } | null;

/**
 * Estado de copiado del link ACTUAL de la fila. Un intento guardado para otra
 * URL (el link se refrescó) no cuenta: vuelve a `idle` hasta que se copie el
 * nuevo. Sin URL no hay nada que copiar.
 */
export function ticketCopyStatus(
  state: TicketCopyState,
  url: string | undefined,
): TicketCopyOutcome | 'idle' {
  if (!state || !url || state.url !== url) return 'idle';
  return state.outcome;
}

/**
 * Intenta copiar el link. Nunca lanza: sin permiso de portapapeles, en un
 * contexto no seguro o sin `navigator.clipboard`, devuelve `failed` para que la
 * UI ofrezca la URL a mano en vez de dejar un botón que no hace nada.
 * `writeText` es inyectable para poder probar el rechazo sin DOM.
 */
export async function copyTicketUrl(
  url: string,
  writeText: (text: string) => Promise<void> = (text) =>
    navigator.clipboard.writeText(text),
): Promise<TicketCopyState> {
  try {
    await writeText(url);
    return { url, outcome: 'copied' };
  } catch {
    return { url, outcome: 'failed' };
  }
}
